import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  GuideAudience,
  IGuideDetail,
  IGuideFilterOptions,
  IGuideListItem,
  IGuideListParams,
} from "@/types/app-guides.interface";
import {
  API_GUIDE_BY_SLUG,
  API_GUIDE_VIEW,
  API_GUIDES,
  API_GUIDES_FILTERS,
} from "@/constants/apiEndpoints";

/** Cache lifetimes (seconds) after the last component using the data unmounts. */
const LIST_TTL = 300;
// Filter options and a guide's content change only when an admin edits them.
const STATIC_TTL = 600;

type GuideKey = { slug: string; audience: GuideAudience };

/**
 * Application guides — read-only help content for My Space and Workspace.
 *
 * Reads are cached and can be prefetched freely: the detail read does not
 * count a view; `recordGuideView` does, once per guide shown.
 */
export const appGuidesApi = createApi({
  reducerPath: "appGuidesApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  keepUnusedDataFor: LIST_TTL,
  endpoints: (builder) => ({
    getGuides: builder.query<IApiPaginatedResponse<IGuideListItem[]>, IGuideListParams>({
      query: (params) => ({ url: API_GUIDES, method: "GET", params }),
    }),
    getGuideFilters: builder.query<IApiResponse<IGuideFilterOptions>, { audience: GuideAudience }>({
      query: (params) => ({ url: API_GUIDES_FILTERS, method: "GET", params }),
      keepUnusedDataFor: STATIC_TTL,
    }),
    getGuideBySlug: builder.query<IApiResponse<IGuideDetail>, GuideKey>({
      query: ({ slug, audience }) => ({
        url: API_GUIDE_BY_SLUG(slug),
        method: "GET",
        params: { audience },
      }),
      keepUnusedDataFor: STATIC_TTL,
    }),
    recordGuideView: builder.mutation<void, GuideKey>({
      query: ({ slug, audience }) => ({
        url: API_GUIDE_VIEW(slug),
        method: "POST",
        params: { audience },
      }),
    }),
  }),
});

export const {
  useGetGuidesQuery,
  useGetGuideFiltersQuery,
  useGetGuideBySlugQuery,
  useRecordGuideViewMutation,
  usePrefetch: useGuidesPrefetch,
} = appGuidesApi;
